import { BUSINESS_KIND_LIST_LABELS, LEAD_SEGMENT_LIST_LABELS, type Permission } from '@hearth/shared';
import {
  Briefcase,
  CalendarDays,
  ChefHat,
  Compass,
  Copy,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  NotebookPen,
  ScrollText,
  Settings,
  SquareCheckBig,
  Store,
  Target,
  Upload,
  UserCheck,
  Warehouse,
  Workflow,
} from 'lucide-react';

/** Keys match the folders under `src/features/` whose routes.tsx may export `useNavCount`. */
export type NavKey =
  | 'dashboard'
  | 'leads'
  | 'prospects'
  | 'clients'
  | 'dealers'
  | 'reps'
  | 'test-kitchens'
  | 'discovery'
  | 'tasks'
  | 'calendar'
  | 'opportunities'
  | 'email'
  | 'automation'
  | 'duplicates'
  | 'imports'
  | 'notes'
  | 'settings'
  | 'audit';

export type NavItem = {
  key: NavKey;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hidden unless the user has this permission. */
  permission?: Permission;
  /** Two-key shortcut shown in the command palette, e.g. "G L". */
  hotkey?: string;
  /** Extra words the command palette matches, e.g. "feedback" for Notes. */
  keywords?: string;
};

export type NavSection = { label: string | null; items: NavItem[] };

export const NAV_SECTIONS: NavSection[] = [
  {
    label: null,
    items: [
      { key: 'dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard, hotkey: 'G D' },
      { key: 'leads', label: BUSINESS_KIND_LIST_LABELS.pizzeria, to: '/leads', icon: Store, hotkey: 'G L' },
      { key: 'prospects', label: LEAD_SEGMENT_LIST_LABELS.prospect, to: '/prospects', icon: Target, keywords: 'prospects engaged pipeline warm' },
      { key: 'clients', label: LEAD_SEGMENT_LIST_LABELS.client, to: '/clients', icon: UserCheck, keywords: 'clients customers accounts' },
      {
        key: 'dealers',
        label: BUSINESS_KIND_LIST_LABELS.dealer,
        to: '/dealers',
        icon: Warehouse,
        keywords: 'dealer distributors resellers equipment dealers',
      },
      {
        key: 'reps',
        label: BUSINESS_KIND_LIST_LABELS.rep,
        to: '/reps',
        icon: Handshake,
        keywords: 'rep agencies representatives sales agents territory',
      },
      {
        key: 'test-kitchens',
        label: LEAD_SEGMENT_LIST_LABELS.test_kitchen,
        to: '/test-kitchens',
        icon: ChefHat,
        keywords: 'test kitchens trial demo kitchen showcase',
      },
      { key: 'discovery', label: 'Discover', to: '/discover', icon: Compass },
      { key: 'tasks', label: 'Tasks', to: '/tasks', icon: SquareCheckBig, hotkey: 'G T' },
      {
        key: 'calendar',
        label: 'Calendar',
        to: '/calendar',
        icon: CalendarDays,
        hotkey: 'G C',
        keywords: 'schedule agenda meetings events appointments availability',
      },
      { key: 'opportunities', label: 'Opportunities', to: '/opportunities', icon: Briefcase, hotkey: 'G O' },
    ],
  },
  {
    label: 'Outreach',
    items: [
      { key: 'email', label: 'Email', to: '/email', icon: Mail },
      { key: 'automation', label: 'Automation', to: '/automation', icon: Workflow },
    ],
  },
  {
    label: 'Data',
    items: [
      { key: 'duplicates', label: 'Duplicates', to: '/duplicates', icon: Copy },
      { key: 'imports', label: 'Imports', to: '/imports', icon: Upload },
    ],
  },
  {
    label: 'Team',
    items: [{ key: 'notes', label: 'Notes', to: '/notes', icon: NotebookPen, keywords: 'feedback ideas feature requests tickets problems questions admin' }],
  },
  {
    label: 'Admin',
    items: [
      { key: 'settings', label: 'Settings', to: '/settings', icon: Settings },
      { key: 'audit', label: 'Audit log', to: '/audit', icon: ScrollText, permission: 'audit:read' },
    ],
  },
];
